import { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, CardTitle, Offcanvas, OffcanvasBody, OffcanvasHeader, Spinner } from 'reactstrap';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { MODALS } from '@/configs/modals';
import { useModals } from '@/hooks/useModals';
import { getCentersOptions } from '@/utils/selectors';
import { convertFileToBase64, showErrors, SLUG_REGEX } from '@/utils/helpers';
import { fetchCenters } from '@/store/centers/actions';
import { addNutritionDiploma, deleteNutritionDiploma, fetchNutritionFeatures, fetchNutritionFilters, fetchNutritionistApi, manageNutritionist } from '@/store/nutrition/actions';
import FormikInput from '@/components/FormikInput';
import FormikPassword from '@/components/FormikPassword';
import FormikCheckbox from '@/components/FormikCheckbox';
import FormikCheckboxGroup from '@/components/FormikCheckboxGroup';
import FormikReactSelect from '@/components/FormikSelect';
import FormikPhotoUpload from '@/components/FormikPhotoUpload';
import FormikVideoUpload from '@/components/FormikVideoUpload';
import FormikSlugInput from '@/components/FormikSlugInput';

const groupFilters = (items = []) => items.reduce((groups, item) => {
  if (item?.is_active) groups[item.type] = [...(groups[item.type] || []), { value: item.id, label: item.text }];
  return groups;
}, {});

const isEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const NutritionistManageDrawer = () => {
  const modalId = MODALS.nutritionist;
  const { params, modalClose } = useModals(modalId);
  const { isOpen, id } = params || {};
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [nutritionist, setNutritionist] = useState({});
  const [filters, setFilters] = useState({});
  const { data: features = [] } = useSelector((state) => state.nutrition.features);
  const centers = useSelector(getCentersOptions) || [];

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchNutritionFeatures());
    dispatch(fetchCenters());
    fetchNutritionFilters().payload.then((items) => setFilters(groupFilters(items))).catch(() => setFilters({}));
    if (id) fetchNutritionistApi(id).then(setNutritionist).catch(() => setNutritionist({}));
  }, [dispatch, id, isOpen]);

  const featureOptions = useMemo(() => features.map((item) => ({ value: item.id, label: item.name })), [features]);
  const initialValues = useMemo(() => ({
    user: { username: nutritionist?.user?.username || '', email: nutritionist?.user?.email || '', phone: nutritionist?.user?.phone || '', password: '' },
    activate: nutritionist?.activate || false,
    is_tester: nutritionist?.is_tester || false,
    telegram_id: nutritionist?.telegram_id || '', ordering: nutritionist?.ordering || 0,
    experience: dayjs(nutritionist?.experience || undefined).format('YYYY-MM-DD'),
    fullname_uk: nutritionist?.fullname_uk || '', fullname_ru: nutritionist?.fullname_ru || '',
    slug: nutritionist?.slug || '',
    description_uk: nutritionist?.description_uk || '', description_ru: nutritionist?.description_ru || '',
    keywords_uk: nutritionist?.keywords_uk || '', keywords_ru: nutritionist?.keywords_ru || '',
    seo_title_uk: nutritionist?.seo_title_uk || '', seo_title_ru: nutritionist?.seo_title_ru || '',
    seo_description_uk: nutritionist?.seo_description_uk || '', seo_description_ru: nutritionist?.seo_description_ru || '',
    higher_education_uk: nutritionist?.higher_education_uk || '', higher_education_ru: nutritionist?.higher_education_ru || '',
    professional_courses_uk: nutritionist?.professional_courses_uk || '', professional_courses_ru: nutritionist?.professional_courses_ru || '',
    another_uk: nutritionist?.another_uk || '', another_ru: nutritionist?.another_ru || '',
    filter_ids: nutritionist?.filter_ids || [], features: nutritionist?.features || [], centers: nutritionist?.centers || [],
    photo: '', photoInit: nutritionist?.photo?.sm || '', video_desktop: nutritionist?.video_desktop || '', video_mobile: nutritionist?.video_mobile || '',
    diplomas: [],
  }), [nutritionist]);
  const close = () => { setNutritionist({}); modalClose(modalId); };
  const refreshProfile = () => id && fetchNutritionistApi(id).then(setNutritionist);
  const uploadDiplomas = async (event) => {
    const files = Array.from(event.target.files || []);
    await Promise.all(files.map(async (file) => addNutritionDiploma({ data: { nutritionist: Number(id), file: await convertFileToBase64(file) }, dispatch })));
    refreshProfile();
  };
  const submit = async (values, formik) => {
    const normalizeValue = async (field, value) => {
      if (['photo', 'video_desktop', 'video_mobile'].includes(field) && value && typeof value === 'object') return convertFileToBase64(value);
      if (field === 'experience' && value) return dayjs(value).format();
      return value;
    };
    const payload = {};

    for (const [field, value] of Object.entries(values)) {
      if (['photoInit', 'diplomas', 'user'].includes(field)) continue;
      if (!isEdit && ['video_desktop', 'video_mobile'].includes(field) && !value) continue;
      if (!isEdit || !isEqual(value, initialValues[field])) payload[field] = await normalizeValue(field, value);
    }

    const user = Object.entries(values.user || {}).reduce((changedUser, [field, value]) => {
      if (field === 'password' ? value : !isEdit || !isEqual(value, initialValues.user?.[field])) changedUser[field] = value;
      return changedUser;
    }, {});
    if (Object.keys(user).length) payload.user = user;

    if (!isEdit) {
      const diplomas = await Promise.all((values.diplomas || []).map((file) => convertFileToBase64(file)));
      payload.diplomas = diplomas.map((file) => ({ file }));
    }

    if (isEdit && !Object.keys(payload).length) {
      toast.success(t('nutrition.saved'));
      close();
      return;
    }

    await manageNutritionist({ id, data: payload, dispatch, onSuccess: () => { toast.success(t('nutrition.saved')); close(); }, onError: (error) => showErrors(error, formik) });
  };
  return <Offcanvas isOpen={isOpen} toggle={close} direction="end" className={modalId} style={{ width: '720px' }}>
    <OffcanvasHeader toggle={close}>{isEdit ? t('nutrition.edit') : t('nutrition.add')}</OffcanvasHeader>
    <OffcanvasBody>{isOpen && <Formik initialValues={initialValues} enableReinitialize validate={({ slug }) => (slug && !SLUG_REGEX.test(slug) ? { slug: t('validation.slug') } : {})} onSubmit={submit}>{({ isSubmitting, setFieldValue }) => <Form>
      <Card><CardHeader><CardTitle>{t('nutrition.account')}</CardTitle></CardHeader><CardBody><div className="fields-grid-2">
        <FormikInput name="user.username" label="Email / username" disabled={isEdit} /><FormikInput name="user.email" label="Email" disabled={isEdit} />
        <FormikInput name="user.phone" label={t('phone')} disabled={isEdit} /><FormikPassword name="user.password" label={t('password')} />
      </div></CardBody></Card>
      <Card><CardHeader><CardTitle>{t('nutrition.profile')}</CardTitle></CardHeader><CardBody>
        <FormikPhotoUpload name="photo" initName="photoInit" label={t('photo')} uploadType="file" />
        <div className="fields-grid-2 mt-3"><FormikCheckbox name="activate" label={t('nutrition.active')} /><FormikCheckbox name="is_tester" label={t('nutrition.tester')} />
          <FormikInput name="telegram_id" label="Telegram" /><FormikInput type="number" name="ordering" label={t('ordering')} />
          <FormikInput name="fullname_uk" label="Ім'я (uk)" /><FormikInput name="fullname_ru" label="Имя (ru)" />
          <FormikSlugInput sourceNames={['fullname_uk']} /><div />
          <FormikInput type="textarea" name="description_uk" label="Опис (uk)" /><FormikInput type="textarea" name="description_ru" label="Описание (ru)" />
          <FormikInput name="experience" type="date" label={t('nutrition.experience')} required />
          <FormikReactSelect isMulti name="features" label={t('nutrition.features')} options={featureOptions} /><FormikReactSelect isMulti name="centers" label={t('centers.title')} options={centers} />
        </div>
        {Object.entries(filters).map(([type, options]) => <FormikCheckboxGroup key={type} name="filter_ids" title={type} items={options} typeValue="number" />)}
      </CardBody></Card>
      <Card><CardHeader><CardTitle>{t('psychologist.modal.video')}</CardTitle></CardHeader><CardBody><div className="fields-grid-2"><FormikVideoUpload name="video_desktop" label="Desktop" /><FormikVideoUpload name="video_mobile" label="Mobile" /></div></CardBody></Card>
      <Card><CardHeader><CardTitle>{t('nutrition.education')}</CardTitle></CardHeader><CardBody><div className="fields-grid-2">
        <FormikInput type="textarea" name="higher_education_uk" label="Вища освіта (uk)" /><FormikInput type="textarea" name="higher_education_ru" label="Высшее образование (ru)" />
        <FormikInput type="textarea" name="professional_courses_uk" label="Курси (uk)" /><FormikInput type="textarea" name="professional_courses_ru" label="Курсы (ru)" />
        <FormikInput type="textarea" name="another_uk" label="Інше (uk)" /><FormikInput type="textarea" name="another_ru" label="Другое (ru)" />
      </div></CardBody></Card>
      <Card><CardHeader><CardTitle>SEO (uk)</CardTitle></CardHeader><CardBody>
        <FormikInput name="seo_title_uk" label={`${t('seo_title')} (uk)`} />
        <div className="fields-grid-2"><FormikInput type="textarea" name="seo_description_uk" label={`${t('seo_description')} (uk)`} /><FormikInput type="textarea" name="keywords_uk" label={`${t('seo_keywords')} (uk)`} /></div>
      </CardBody></Card>
      <Card><CardHeader><CardTitle>SEO (ru)</CardTitle></CardHeader><CardBody>
        <FormikInput name="seo_title_ru" label={`${t('seo_title')} (ru)`} />
        <div className="fields-grid-2"><FormikInput type="textarea" name="seo_description_ru" label={`${t('seo_description')} (ru)`} /><FormikInput type="textarea" name="keywords_ru" label={`${t('seo_keywords')} (ru)`} /></div>
      </CardBody></Card>
      <Card><CardHeader><CardTitle>{t('nutrition.diplomas')}</CardTitle></CardHeader><CardBody>{isEdit ? <><div className="d-flex flex-wrap gap-2 mb-3">{(nutritionist.diplomas || []).map((diploma) => <div key={diploma.id}><a href={diploma.file?.original} target="_blank" rel="noreferrer"><img src={diploma.file?.thumbnail || diploma.file?.original} alt="" style={{ width: 80, height: 80, objectFit: 'cover' }} /></a><Button size="sm" color="link" onClick={() => dispatch(deleteNutritionDiploma(diploma.id, { t, onSuccess: refreshProfile }))}>×</Button></div>)}</div><input type="file" multiple accept="image/*" onChange={uploadDiplomas} /></> : <input type="file" multiple accept="image/*" onChange={(event) => setFieldValue('diplomas', Array.from(event.target.files))} />}</CardBody></Card>
      <div className="d-flex justify-content-end gap-2"><Button color="light" type="button" onClick={close}>{t('btn_cancel')}</Button><Button color="primary" type="submit" disabled={isSubmitting}>{isSubmitting && <Spinner size="sm" />} {t('btn_save')}</Button></div>
    </Form>}</Formik>}</OffcanvasBody>
  </Offcanvas>;
};
export default NutritionistManageDrawer;
